import { Divider } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';
import { useMatch } from 'react-router';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  BaseThreadFragmentFragmentGQLType,
  GetEventLogsQueryGQLType,
  ThreadActionGQLType,
  InvoiceEntityMetaFragmentFragmentGQLType,
  EventLogTypeGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { basicStringSearch } from 'packages/event-logs/helpers';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { PermissionsFn } from '~/core/PermissionGuard';
import * as CreditFacilityEntityConfig from '~/core/routes/credit-facilities/EntityConfig';
import { Thread } from '~/packages/comments/Thread';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UserDetail } from '~/packages/common-frontend/components/UserDetail';
import { EntityName as BaseEntityName } from '~/packages/event-logs/EntityName';
import { EventFeedItem as BaseEventFeedItem } from '~/packages/event-logs/EventFeedItem';
import {
  EventLogFilter,
  useEventLogFilters,
} from '~/packages/event-logs/EventLogFilters';
import { useGetInvoiceFiltersQuery } from '~/packages/event-logs/queries/entityFilters.gql.generated';
import { EventLogExportDataRow } from '~/packages/event-logs/types';

// Constants
export const EVENT_LOG_TYPES: EventLogTypeGQLType[] = [
  'invoice_cancel',
  'invoice_create',
  'invoice_editDueDate',
  'invoice_paid',
  'invoice_thread_create',
  'invoice_undoPayment',
];

// Types
export type EntityMeta = InvoiceEntityMetaFragmentFragmentGQLType;
type EventLogData = GetEventLogsQueryGQLType['eventLogs']['items'][number];
type ThreadData = BaseThreadFragmentFragmentGQLType;

// Exports
export const permissions: PermissionsFn = (context) => {
  if (context.user.organization.slug === 'valleyBank') return 'deny';
  return context.flags.productsInvoicesPermissions;
};

type EntityNameProps = Pick<EntityMeta, 'invoiceId'> &
  Partial<Pick<EntityMeta, 'dueDate' | 'invoiceStatus' | 'totalAmount'>>;

export const EntityName = ({
  invoiceId,
  dueDate,
  invoiceStatus,
  totalAmount,
}: EntityNameProps) => {
  const match = useMatch({
    path: '/invoices/:invoiceId',
    end: true,
  });

  const isOnInvoiceRoute = match?.params.invoiceId === invoiceId;

  const resolvedName = `Invoice ...${invoiceId.slice(-12)}`;
  return (
    <BaseEntityName
      to={`/invoices/${invoiceId}`}
      isOnEntityRoute={isOnInvoiceRoute}
      label={!isOnInvoiceRoute ? resolvedName : 'Invoice'}
      tooltipLabel={
        <>
          Invoice ID: {invoiceId}
          {totalAmount ? (
            <>
              <br />
              Invoice total amount:{' '}
              {commonUtils.numberToCurrencyWithSymbol({ number: totalAmount })}
            </>
          ) : null}
          {invoiceStatus ? (
            <>
              <br />
              Invoice status: {invoiceStatus}
            </>
          ) : null}
          {dueDate ? (
            <>
              <br />
              Invoice Due Date: {dueDate}
            </>
          ) : null}
        </>
      }
    />
  );
};

type ThreadFeedItemProps = {
  thread: ThreadData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const ThreadFeedItem = ({
  thread,
  onThreadAction,
  onMount,
  onReady,
}: ThreadFeedItemProps) => {
  switch (thread.domain) {
    case 'invoice': {
      return (
        <Thread
          key={thread.threadId}
          threadId={thread.threadId}
          onThreadAction={onThreadAction}
          onReady={() => onReady?.(thread.threadId)}
          onMount={() => onMount?.(thread.threadId)}
        >
          <Typography inline variant="detail-muted">
            Thread on{' '}
            <EntityName
              invoiceId={thread.domainIdentifier}
              // !TODO: switch to entity metadata
              // name={thread.entityMetadata.name}
            />
          </Typography>
          <Divider />
        </Thread>
      );
    }
    default: {
      return null;
    }
  }
};

type EventFeedItemProps = {
  log: EventLogData;
  onThreadAction?: (action: ThreadActionGQLType) => void;
  onMount?: (id: string) => void;
  onReady?: (id: string) => void;
};
export const EventFeedItem = ({
  log,
  onThreadAction,
  onMount,
  onReady,
}: EventFeedItemProps) => {
  switch (log.__typename) {
    case 'InvoiceThreadCreateEvent': {
      return (
        <Thread
          key={log.eventId}
          threadId={log.current.id}
          onThreadAction={onThreadAction}
          onMount={() => onMount?.(log.current.id)}
          onReady={() => onReady?.(log.current.id)}
        >
          <Typography inline variant="detail-muted">
            Thread on <EntityName {...log.entityMetadata} /> for{' '}
            <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
          </Typography>
          <Divider />
        </Thread>
      );
    }

    case 'InvoiceCreatedEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} /> created{' '}
          <EntityName {...log.entityMetadata} /> for{' '}
          <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
        </BaseEventFeedItem>
      );
    }

    case 'InvoiceUpdatedEvent': {
      return (
        <BaseEventFeedItem
          key={log.eventId}
          createdAt={log.createdAt}
          leftSlot={<UserDetail variant="avatar" user={log.actor} />}
        >
          <UserDetail variant="name" user={log.actor} />{' '}
          {log.entityMetadata.invoiceStatus}{' '}
          <EntityName {...log.entityMetadata} /> for{' '}
          <CreditFacilityEntityConfig.EntityName {...log.creditFacility} />
        </BaseEventFeedItem>
      );
    }

    default: {
      return null;
    }
  }
};

export const EntityFilter = () => {
  const selectedIds = useEventLogFilters((s) => s.invoiceIds);
  const onSelect = useEventLogFilters((s) => s.setInvoiceIds);

  const { user } = useUserAuthOrThrow();
  const [searchString, setSearchString] = useState<string>('');

  const entityFiltersQuery = useGetInvoiceFiltersQuery({
    fetchPolicy: 'cache-and-network',
    variables: { organizationId: user.organizationId },
  });

  const entities: EntityMeta[] = useMemo(
    () =>
      entityFiltersQuery.data?.invoices.map((invoice) => ({
        ...invoice,
        invoiceId: invoice.id,
        __typename: 'InvoiceEntityMeta',
        loanNames: [],
        externalLoanIds: [],
      })) ?? [],
    [entityFiltersQuery.data?.invoices],
  );

  const options = useMemo(
    () =>
      entities
        .map((entity) => ({
          id: entity.invoiceId,
          textLabel: `Invoice ...${entity.invoiceId.slice(-12)}`,
          data: entity,
        }))
        .filter((option) => basicStringSearch(searchString, option.textLabel))
        .slice(0, 20),
    [entities, searchString],
  );

  if (entityFiltersQuery.error) {
    Sentry.captureException(entityFiltersQuery.error);
    return null;
  }
  if (entities.length < 1) return null;

  return (
    <EventLogFilter
      onSearchInputChange={setSearchString}
      leftIcon={<Icon icon="file-invoice-dollar" />}
      minWidth="10rem"
      noneLabel="Any Invoice"
      singularLabel="Invoice"
      pluralLabel="Invoices"
      selectedIds={selectedIds}
      options={options}
      onClose={onSelect}
    />
  );
};

/* eslint-disable camelcase */
export const toExportDataRow = (
  dataRow: EventLogExportDataRow,
  log: EventLogData,
): EventLogExportDataRow => {
  switch (log.__typename) {
    case 'InvoiceThreadCreateEvent': {
      return {
        ...dataRow,
        // InvoiceEntityMetaFragment
        invoice_meta_dueDate: log.entityMetadata.dueDate,
        invoice_meta_invoiceStatus: log.entityMetadata.invoiceStatus,
        invoice_meta_totalAmount: log.entityMetadata.totalAmount,
        loan_names: log.entityMetadata.loanNames.join(', '),
        loan_externalIds: log.entityMetadata.externalLoanIds.join(', '),
      };
    }

    case 'InvoiceCreatedEvent':
    case 'InvoiceUpdatedEvent': {
      const nextDataRow = {
        ...dataRow,
        // InvoiceEntityMetaFragment
        invoice_meta_dueDate: log.entityMetadata.dueDate,
        invoice_meta_invoiceStatus: log.entityMetadata.invoiceStatus,
        invoice_meta_totalAmount: log.entityMetadata.totalAmount,
        loan_names: log.entityMetadata.loanNames.join(', '),
        loan_externalIds: log.entityMetadata.externalLoanIds.join(', '),

        // InvoiceStatusEventsCommonSnapshotFragment
        invoice_status_dueDate: log.current.dueDate,
        invoice_status_invoiceStatus: log.current.invoiceStatus,
        invoice_status_totalAmount: log.current.totalAmount,
      };

      return nextDataRow;
    }

    default: {
      return dataRow;
    }
  }
};
/* eslint-enable camelcase */
